import React from 'react';

/**
 * renderQuotedValue
 * -----------------
 * Detects the Python backend's `Title: "patient answer"` quoting pattern and
 * renders the quoted portion in italic curly quotes so the patient's own words
 * are visually distinct from the question title.
 *
 * The CI and informant prefill pipelines consistently wrap the patient's answer
 * in double quotes:
 *   f'{question_title}: "{patient_value}"'
 *
 * Other pipelines (PAD goals, lifestyle, health checks) do NOT use this quoting
 * pattern, so the helper falls through to plain text when no match is found.
 *
 * Edge-case handling:
 *  - Embedded double quotes inside patient_value: the regex is greedy on the
 *    closing quote, matching the LAST `"` in the string so the outermost pair
 *    is always used.
 *  - Multiline content (bullet lists from selectMultiple): handled by the `s`
 *    (dotall) flag so `.` matches newlines.
 *  - Empty quoted content (`Title: ""`): no styling applied.
 *  - Null/undefined/empty input: returns null.
 *
 * @param {string} text - The input.value string from a model input entry
 * @returns {React.ReactNode} Formatted JSX or plain text
 */
export const renderQuotedValue = (text) => {
  if (!text) return null;

  // Match: "Question title: "patient answer content""
  // The (.+) before the closing " is greedy so it captures everything up to
  // the LAST double-quote, handling embedded quotes in patient answers.
  const match = text.match(/^(.+?):\s*"(.+)"$/s);

  if (!match) {
    return text;
  }

  const questionTitle = match[1];
  const quotedContent = match[2];

  // Don't style empty or whitespace-only quoted content
  if (!quotedContent.trim()) {
    return text;
  }

  return (
    <>
      {questionTitle}:{' '}
      <span className="italic">{'\u201c'}{quotedContent}{'\u201d'}</span>
    </>
  );
};

/**
 * compareStructuredEntryKeys
 * --------------------------
 * Sort comparator for the AI inputs panel, so the cards appear in the order
 * the clinician saw the questions on the proforma page rather than in the
 * order Firestore happens to return the map keys (Firestore maps are
 * unordered, so the backend's insertion order is not preserved).
 *
 * How it works, in plain terms: the backend keys each input by its position
 * in the data model, e.g. "5_3_1" means section 5, panel 3, question 1. The
 * comparator splits each key on "_" and compares the pieces one at a time.
 * Pieces that are both numbers are compared as numbers (so "5_3_2" comes
 * before "5_3_10", which a plain string sort would get wrong); any other
 * pieces are compared as text. A key that runs out of pieces first sorts
 * first, so a panel's notes key ("5_3") sits above its question keys
 * ("5_3_1", "5_3_2", ...).
 *
 * The comparator never assumes a particular key shape, so it is safe for
 * every stage: adult SSI keys such as "symptom_2_persisted" sort by the
 * embedded symptom number. CYP Diagnostic Decision keys
 * ("criterion_inattentionThreshold", "diagnosisDecision") follow the
 * Decision for ADHD page. Any other word-shaped key falls back to a
 * stable alphabetical order.
 *
 * CYP ADHD SSI keys are words after the symptom number ("cyp_ssi_3_settings",
 * "cyp_ssi_3_home_examples"). A plain split would list those cards
 * alphabetically, which is not the order of the interview. When both keys
 * belong to that interview, the symptom or area is compared first and the
 * field is then compared against the order of the page.
 *
 * It takes [key, input] pairs because that is what Array.prototype.sort
 * receives when sorting the result of Object.entries().
 *
 * @param {[string, Object]} entryA - [key, input] pair
 * @param {[string, Object]} entryB - [key, input] pair
 * @returns {number} negative when A sorts first, positive when B sorts first
 */
// Order of a CYP ADHD SSI card within one paragraph, matching the interview
// page from top to bottom. Onset sits left of the six-month question, then
// the Home and Education chips, then the Home example column, then the
// Education example column. A domain paragraph is the impairment decision,
// then any older overall level, then the examples, then the notes.
// A symptom and an impairment area both have an "examples" answer and a
// notes answer, but they do not sit in the same place on the two pages, so
// each page has its own order.
const CYP_SSI_FIELD_ORDER = {
  symptom: [
    'onset',
    'persisted',
    'settings',
    'home_examples',
    'home_other',
    'education_examples',
    'education_other',
    'examples',
    'notes',
    'home_notes',
    'education_notes',
    'school_report',
    'guardian_report',
  ],
  domain: [
    'impaired',
    'level',
    'examples',
    'notes',
  ],
};

const CYP_SSI_FIELD_RANK = Object.fromEntries(
  Object.entries(CYP_SSI_FIELD_ORDER).map(([kind, fields]) => [
    kind,
    fields.reduce((ranks, field, index) => {
      ranks[field] = index;
      return ranks;
    }, {}),
  ]),
);

/**
 * Which symptom or impairment area a CYP SSI input belongs to, and which
 * answer it is. Null for every other pathway, so those keys keep the
 * general comparison below.
 */
function cypSsiIdentity(key) {
  const text = String(key);
  const symptom = /^cyp_ssi_(\d+)_(.+)$/.exec(text);
  if (symptom) {
    return { kind: 'symptom', id: symptom[1], field: symptom[2] };
  }
  const domain = /^cyp_ssi_FI_([A-Za-z]+)_(.+)$/.exec(text);
  if (domain) {
    return { kind: 'domain', id: domain[1], field: domain[2] };
  }
  return null;
}

// Order of a CYP Diagnostic Decision card, matching the Decision for ADHD
// page from top to bottom: Criterion A (its parts, then the two symptom
// counts), B, C, D, E, then the diagnostic decision and the questions that
// follow it. Firestore does not keep the order these were written in, and
// a plain alphabetical sort puts "across settings" above "inattention".
const CYP_DD_DECISION_ORDER = [
  'criterion_ageUnder17',
  'criterion_inattentionThreshold',
  'criterion_hyperactivityThreshold',
  'criterion_sixMonths',
  'criterion_developmentallyInappropriate',
  'criterion_onsetBeforeTwelve',
  'criterion_acrossSettings',
  'criterion_functionalImpairment',
  'criterion_notBetterExplained',
  'diagnosisDecision',
  'diagnosisPresentation',
  'diagnosisSeverity',
  'partialDiagnosisClassification',
  'otherSpecifiedReason',
  'otherSpecifiedReasonText',
  'supportedByCollateral',
  'diagnosisRationale',
  'remissionSpecifier',
  'supportingEvidence',
  'supportingEvidenceNotes',
];

const CYP_DD_DECISION_RANK = CYP_DD_DECISION_ORDER.reduce((ranks, field, index) => {
  ranks[field] = index;
  return ranks;
}, {});

/**
 * Compare two CYP Diagnostic Decision input keys. Null when either key is
 * not one of those decisions, so every other pathway keeps the general
 * comparison below.
 */
function compareCypDdKeys(keyA, keyB) {
  const rankA = CYP_DD_DECISION_RANK[String(keyA)];
  const rankB = CYP_DD_DECISION_RANK[String(keyB)];
  if (rankA === undefined || rankB === undefined) return null;
  return rankA - rankB;
}

function compareCypSsiKeys(keyA, keyB) {
  const a = cypSsiIdentity(keyA);
  const b = cypSsiIdentity(keyB);
  if (!a || !b) return null;
  if (a.kind !== b.kind) return a.kind === 'symptom' ? -1 : 1;
  if (a.id !== b.id) {
    const bothNumeric = /^\d+$/.test(a.id) && /^\d+$/.test(b.id);
    if (bothNumeric) return Number(a.id) - Number(b.id);
    return a.id.localeCompare(b.id);
  }
  const ranks = CYP_SSI_FIELD_RANK[a.kind];
  const rankA = ranks[a.field];
  const rankB = ranks[b.field];
  if (rankA !== undefined && rankB !== undefined && rankA !== rankB) {
    return rankA - rankB;
  }
  if (rankA !== undefined && rankB === undefined) return -1;
  if (rankA === undefined && rankB !== undefined) return 1;
  return a.field.localeCompare(b.field);
}

/**
 * omitCypSsiEnvironmentMet
 * ------------------------
 * Drops the "Home environment — Met" and "Education environment — Met"
 * cards. Those answers are the Home and Education chips, which the panel
 * already shows as "Settings where it is seen". Older saved inputs still
 * carry the extra cards until the paragraph is drafted again, so the review
 * page hides them on display as well.
 *
 * Returns the same object when there is nothing to drop.
 */
export function omitCypSsiEnvironmentMet(inputs) {
  if (!inputs || typeof inputs !== 'object') return inputs;
  const kept = {};
  let dropped = false;
  Object.entries(inputs).forEach(([key, value]) => {
    if (/^cyp_ssi_\d+_(?:home|education)_met$/.test(String(key))) {
      dropped = true;
      return;
    }
    kept[key] = value;
  });
  return dropped ? kept : inputs;
}

export function compareStructuredEntryKeys([keyA], [keyB]) {
  const cypOrder = compareCypSsiKeys(keyA, keyB);
  if (cypOrder !== null && cypOrder !== 0) return cypOrder;
  const cypDdOrder = compareCypDdKeys(keyA, keyB);
  if (cypDdOrder !== null && cypDdOrder !== 0) return cypDdOrder;

  const partsA = String(keyA).split('_');
  const partsB = String(keyB).split('_');
  const len = Math.max(partsA.length, partsB.length);
  for (let i = 0; i < len; i += 1) {
    // A key with fewer pieces (a parent) sorts before a longer one (a child).
    if (i >= partsA.length) return -1;
    if (i >= partsB.length) return 1;
    const a = partsA[i];
    const b = partsB[i];
    // Only treat a piece as a number when the WHOLE piece is digits, so
    // "10a" and "1" are compared as text rather than as 10 and 1.
    const bothNumeric = /^\d+$/.test(a) && /^\d+$/.test(b);
    if (bothNumeric) {
      const diff = Number(a) - Number(b);
      if (diff !== 0) return diff;
    } else {
      const cmp = a.localeCompare(b);
      if (cmp !== 0) return cmp;
    }
  }
  return 0;
}

/**
 * renderModelInput
 * ----------------
 * Shared renderer for the AI inputs side panel across all Review Documents
 * pages. Displays each input entry as a gray card with the value text and an
 * optional source attribution line.
 *
 * Three rendering modes for the value:
 *  1. "Your checked examples": splits on ';' and renders as a bullet list
 *  2. Default: applies renderQuotedValue to style patient prefill answers
 *
 * Two source display modes (controlled by options.articleLinks):
 *  - false (default): plain "in {source}" text
 *  - true: URLs are rendered as clickable "Link to article" links
 *
 * Ordering (controlled by options.sortEntries):
 *  - absent (default): cards appear in the order Object.entries() yields,
 *    which is whatever order Firestore returned the map in. Every existing
 *    caller relies on this, so it is the default.
 *  - a comparator (e.g. compareStructuredEntryKeys): cards are sorted with
 *    it before rendering. Opt-in per page so a pathway can adopt ordered
 *    inputs without changing any other pathway's panel.
 *
 * @param {string} role - The label for this input group (e.g. "Patient's report")
 * @param {Object} inputs - Keyed input entries, each with { value, source }
 * @param {{ articleLinks?: boolean, sortEntries?: Function }} options - Display options
 * @returns {React.ReactNode}
 */
export const renderModelInput = (role, inputs, options = {}) => {
  const { articleLinks = false, sortEntries = null } = options;

  // Only render entries that contributed actual text to the model.  Entries
  // with an empty value but a source attribution (e.g. optional MDT treatment
  // notes left blank) should not show a blank card — they fall through to the
  // section-level "No inputs used" placeholder instead.
  const filteredEntries = Object.entries(inputs || {}).filter(([, input]) => (
    input && String(input.value || '').trim()
  ));

  // Sort only when the caller asked for it; a copy is sorted so the array
  // from Object.entries() is never mutated under another consumer.
  const validEntries = typeof sortEntries === 'function'
    ? [...filteredEntries].sort(sortEntries)
    : filteredEntries;

  if (validEntries.length === 0) {
    return (
      <div className="mb-4">
        <div className="font-bold text-left text-gray-400">{role}:</div>
        <div className="bg-gray-200 rounded-lg p-3 mb-2 border-2 border-gray-300">
          <p className="text-left text-gray-400 text-sm">No inputs used</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <div className="font-bold text-left text-gray-800">{role}:</div>
      {validEntries.map(([key, input]) => {
        // Checked examples are semicolon-delimited and rendered as a list
        if (role === "Your checked examples" && input.value) {
          const examples = input.value.split(';').filter(example => example.trim());

          return (
            <div key={key} className="bg-gray-200 rounded-lg p-3 mb-2 border-2 border-gray-300">
              {examples.length > 0 ? (
                <ul className="list-disc pl-5 text-left text-sm">
                  {examples.map((example, index) => (
                    <li key={index} className="mb-1 whitespace-pre-wrap">{example.trim()}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-left text-sm whitespace-pre-wrap">{input.value}</p>
              )}

              {input.source && (
                <p className="mt-1 text-xs font-semibold text-right italic text-gray-600">
                  in {input.source}
                </p>
              )}
            </div>
          );
        }

        // For article-aware pages, detect URL sources to render as links
        const isArticleLink = articleLinks && input.source &&
          typeof input.source === 'string' &&
          (input.source.startsWith('http://') || input.source.startsWith('https://'));

        return (
          <div key={key} className="bg-gray-200 rounded-lg p-3 mb-2 border-2 border-gray-300">
            <p className="text-left text-sm whitespace-pre-wrap">
              {renderQuotedValue(input.value || '')}
            </p>

            {input.source && (
              <div className="mt-1 text-xs font-semibold text-right italic text-gray-600">
                {isArticleLink ? (
                  <a
                    href={input.source}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    Link to article
                  </a>
                ) : (
                  <span>in {input.source}</span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
